/**
 * Dialog: Auto-Import pelanggan Static IP dari Queue Simple MikroTik.
 * Endpoint: POST /api/v2/mikrotik/{id}/import-from-queues
 * Mode 'dry_run' menampilkan preview tanpa create customer.
 */
import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Router, Download, CheckCircle, AlertCircle, Loader2, ListChecks, FileSpreadsheet } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const ImportFromQueueDialog = ({ open, onClose, onImported }) => {
  const [routers, setRouters] = useState([]);
  const [owners, setOwners] = useState([]);
  const [plans, setPlans] = useState([]);
  const [selectedRouter, setSelectedRouter] = useState('');
  const [defaultOwnerId, setDefaultOwnerId] = useState('');
  const [defaultPlanId, setDefaultPlanId] = useState('');
  const [step, setStep] = useState('idle');     // idle | previewing | preview | importing | done
  const [preview, setPreview] = useState(null);
  const [importResult, setImportResult] = useState(null);
  const [search, setSearch] = useState('');

  useEffect(() => {
    if (!open) return;
    const token = localStorage.getItem('token');
    axios.get(`${API_URL}/api/v2/mikrotiks`, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => setRouters(Array.isArray(r.data?.data) ? r.data.data : (r.data || [])))
      .catch(() => setRouters([]));
    axios.get(`${API_URL}/api/v2/owners`, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => setOwners(Array.isArray(r.data?.data) ? r.data.data : (r.data || [])))
      .catch(() => setOwners([]));
    axios.get(`${API_URL}/api/v2/service-plans`, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => setPlans(Array.isArray(r.data?.data) ? r.data.data : (r.data || [])))
      .catch(() => setPlans([]));
    setStep('idle'); setPreview(null); setImportResult(null);
  }, [open]);

  const handlePreview = async () => {
    if (!selectedRouter) return;
    setStep('previewing'); setPreview(null);
    try {
      const token = localStorage.getItem('token');
      const body = { dry_run: true };
      if (defaultOwnerId) body.default_owner_id = Number(defaultOwnerId);
      if (defaultPlanId) body.default_service_plan_id = Number(defaultPlanId);
      const r = await axios.post(
        `${API_URL}/api/v2/mikrotik/${selectedRouter}/import-from-queues`,
        body,
        { headers: { Authorization: `Bearer ${token}` }, timeout: 60000 }
      );
      setPreview(r.data);
      setStep('preview');
    } catch (e) {
      setPreview({ success: false, error: e.response?.data?.detail || e.message });
      setStep('idle');
    }
  };

  const handleExecute = async () => {
    if (!selectedRouter) return;
    setStep('importing'); setImportResult(null);
    try {
      const token = localStorage.getItem('token');
      const body = { dry_run: false };
      if (defaultOwnerId) body.default_owner_id = Number(defaultOwnerId);
      if (defaultPlanId) body.default_service_plan_id = Number(defaultPlanId);
      const r = await axios.post(
        `${API_URL}/api/v2/mikrotik/${selectedRouter}/import-from-queues`,
        body,
        { headers: { Authorization: `Bearer ${token}` }, timeout: 300000 }
      );
      setImportResult(r.data);
      setStep('done');
      if (onImported) onImported(r.data);
    } catch (e) {
      setImportResult({ success: false, error: e.response?.data?.detail || e.message });
      setStep('done');
    }
  };

  const filteredPreview = (preview?.preview || []).filter(p => {
    if (!search) return true;
    const q = search.toLowerCase();
    return (p.name || '').toLowerCase().includes(q) ||
           (p.current_ip || '').includes(q) ||
           (p.service_plan_name || '').toLowerCase().includes(q);
  });

  const handleExportCsv = () => {
    if (!preview?.preview?.length) return;
    const rows = preview.preview;
    const headers = ['No', 'Nama Queue', 'IP', 'Speed Queue', 'Speed Up Mbps', 'Speed Down Mbps', 'Paket Mapping', 'Plan ID', 'Biaya', 'Connection Type', 'Router ID', 'Tenant ID', 'Branch ID'];
    const lines = [headers.join(';')];
    rows.forEach((r, i) => {
      const cells = [
        i + 1,
        '"' + String(r.name || '').replace(/"/g, '""') + '"',
        r.current_ip || '',
        r.queue_speed_display || '',
        r.queue_speed_up_mbps || 0,
        r.queue_speed_down_mbps || 0,
        '"' + String(r.service_plan_name || '').replace(/"/g, '""') + '"',
        r.service_plan_id || '',
        r.monthly_fee || 0,
        r.connection_type || 'static',
        r.router_id || '',
        r.tenant_id || '',
        r.branch_id || '',
      ];
      lines.push(cells.join(';'));
    });
    const csv = '\ufeff' + lines.join('\r\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    const ts = new Date().toISOString().slice(0, 19).replace(/[:T]/g, '-');
    const routerName = routers.find(r => String(r.id) === String(selectedRouter))?.name || 'router';
    a.download = 'import-preview-' + routerName + '-' + ts + '.csv';
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  const fmtIDR = (n) => 'Rp ' + Number(n || 0).toLocaleString('id-ID');

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-6xl max-h-[92vh] overflow-hidden flex flex-col">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Download className="w-5 h-5 text-emerald-600" />
            Import Pelanggan Static IP dari MikroTik Queue
          </DialogTitle>
        </DialogHeader>

        <div className="grid grid-cols-1 md:grid-cols-4 gap-3 p-3 bg-slate-50 rounded-lg border border-slate-200">
          <select
            data-testid="import-queue-router"
            value={selectedRouter}
            onChange={(e) => { setSelectedRouter(e.target.value); setPreview(null); setStep('idle'); }}
            className="p-2 border border-slate-300 rounded-md bg-white text-sm"
          >
            <option value="">-- Pilih Router --</option>
            {routers.map(r => (
              <option key={r.id} value={r.id}>{r.name} ({r.host})</option>
            ))}
          </select>
          <select
            data-testid="import-queue-owner"
            value={defaultOwnerId}
            onChange={(e) => setDefaultOwnerId(e.target.value)}
            className="p-2 border border-slate-300 rounded-md bg-white text-sm"
          >
            <option value="">-- Owner default (opsional) --</option>
            {owners.map(o => (
              <option key={o.id} value={o.id}>{o.name}</option>
            ))}
          </select>
          <select
            data-testid="import-queue-plan"
            value={defaultPlanId}
            onChange={(e) => setDefaultPlanId(e.target.value)}
            className="p-2 border border-slate-300 rounded-md bg-white text-sm"
          >
            <option value="">-- Paket default: auto-mapping by speed --</option>
            {plans.filter(p => (p.package_type !== 'hotspot' && p.package_type !== 'voucher')).map(p => (
              <option key={p.id} value={p.id}>{p.name} ({p.speed_down || ''}) - Rp {Number(p.price || 0).toLocaleString('id-ID')}</option>
            ))}
          </select>
          <Button
            data-testid="import-queue-preview-btn"
            onClick={handlePreview}
            disabled={!selectedRouter || step === 'previewing' || step === 'importing'}
            className="bg-blue-600 hover:bg-blue-700 text-white"
          >
            {step === 'previewing'
              ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Memuat preview...</>
              : <><ListChecks className="w-4 h-4 mr-2" /> Preview Import</>}
          </Button>
        </div>

        {preview?.error && (
          <div className="p-3 bg-red-50 border border-red-200 rounded-lg flex items-start gap-2 mt-2">
            <AlertCircle className="w-4 h-4 text-red-600 mt-0.5 shrink-0" />
            <span className="text-sm text-red-700">{preview.error}</span>
          </div>
        )}

        {preview?.success && (
          <>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-2 text-xs mt-3">
              <div className="p-3 bg-blue-50 border border-blue-200 rounded-lg">
                <div className="text-[11px] text-blue-700 font-semibold">Total Queue</div>
                <div className="text-2xl font-bold text-blue-900">{preview.total_queues}</div>
              </div>
              <div className="p-3 bg-emerald-50 border border-emerald-200 rounded-lg">
                <div className="text-[11px] text-emerald-700 font-semibold">Akan Diimport</div>
                <div className="text-2xl font-bold text-emerald-900" data-testid="import-queue-will-count">{preview.will_import}</div>
              </div>
              <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg">
                <div className="text-[11px] text-amber-700 font-semibold">Skipped</div>
                <div className="text-2xl font-bold text-amber-900">{preview.skipped_count}</div>
              </div>
              <div className="p-3 bg-rose-50 border border-rose-200 rounded-lg">
                <div className="text-[11px] text-rose-700 font-semibold">Errors</div>
                <div className="text-2xl font-bold text-rose-900">{preview.errors_count}</div>
              </div>
            </div>

            <Input
              data-testid="import-queue-search"
              placeholder="Cari nama / IP / paket..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="mt-2"
            />

            <div className="flex-1 overflow-auto border border-slate-200 rounded-lg mt-2">
              <table className="w-full text-sm">
                <thead className="bg-slate-100 text-slate-700 sticky top-0">
                  <tr>
                    <th className="p-2 text-left">#</th>
                    <th className="p-2 text-left">Nama Queue</th>
                    <th className="p-2 text-left">IP</th>
                    <th className="p-2 text-left">Speed Queue</th>
                    <th className="p-2 text-left">Paket Mapping</th>
                    <th className="p-2 text-right">Biaya</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredPreview.map((p, i) => (
                    <tr key={i} className="border-t border-slate-100 hover:bg-slate-50">
                      <td className="p-2 text-slate-400">{i + 1}</td>
                      <td className="p-2 font-medium text-slate-800">{p.name}</td>
                      <td className="p-2 font-mono text-blue-700">{p.current_ip}</td>
                      <td className="p-2 text-slate-700">{p.queue_speed_display}</td>
                      <td className="p-2 text-slate-700">{p.service_plan_name || <span className="text-rose-500">- belum cocok -</span>}</td>
                      <td className="p-2 text-right text-emerald-700 font-semibold">{fmtIDR(p.monthly_fee)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
              <div className="p-2 text-center text-xs text-slate-500 bg-slate-50 border-t border-slate-200">
                Menampilkan {filteredPreview.length} baris.
              </div>
            </div>

            <div className="flex justify-end gap-2 mt-3 pt-3 border-t border-slate-200">
              <Button variant="outline" onClick={onClose}>Batal</Button>
              <Button
                data-testid="import-queue-export-csv-btn"
                variant="outline"
                onClick={handleExportCsv}
                disabled={!preview?.preview?.length}
                className="border-emerald-600 text-emerald-700 hover:bg-emerald-50"
                title="Download daftar pelanggan sebagai file CSV (bisa dibuka di Excel) tanpa import ke database"
              >
                <FileSpreadsheet className="w-4 h-4 mr-2" />
                Export ke Excel/CSV
              </Button>
              <Button
                data-testid="import-queue-execute-btn"
                onClick={handleExecute}
                disabled={preview.will_import === 0 || step === 'importing'}
                className="bg-emerald-600 hover:bg-emerald-700 text-white"
              >
                {step === 'importing'
                  ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Importing {preview.will_import} pelanggan...</>
                  : <><Download className="w-4 h-4 mr-2" /> Import {preview.will_import} Pelanggan</>}
              </Button>
            </div>
          </>
        )}

        {step === 'done' && importResult && (
          <div className={`p-4 rounded-lg border mt-3 ${importResult.success ? 'bg-emerald-50 border-emerald-200' : 'bg-rose-50 border-rose-200'}`}>
            <div className="flex items-center gap-2 font-semibold">
              {importResult.success
                ? <><CheckCircle className="w-5 h-5 text-emerald-600" /> Import Selesai</>
                : <><AlertCircle className="w-5 h-5 text-rose-600" /> Import Gagal</>}
            </div>
            <div className="text-sm mt-2 text-slate-700">
              {importResult.success ? (
                <>
                  Berhasil menambah <span className="font-bold text-emerald-700">{importResult.created}</span> pelanggan dari{' '}
                  <span className="font-bold">{importResult.total_queues}</span> queue. Skipped: {importResult.skipped_count}, Errors: {importResult.errors_count}.
                </>
              ) : (importResult.error || 'Unknown error')}
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default ImportFromQueueDialog;
